<template>
  <div class="senators-page">
    <div class="page-header">
      <h1>Senators</h1>
      <div class="header-controls">
        <div class="congress-selector">
          <label for="congress">Period of Congress:</label>
          <Select 
            id="congress" 
            v-model="selectedCongress" 
            :options="congressOptions"
            optionLabel="label"
            optionValue="value"
            @change="loadSenators"
          />
        </div>
        <Button 
          label="Add Senator" 
          icon="pi pi-plus" 
          @click="openAddModal"
        />
      </div>
    </div>

    <DataTable 
      :value="senators" 
      :paginator="true" 
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      :loading="loading"
      stripedRows
      showGridlines
      responsiveLayout="scroll"
      :globalFilterFields="['name', 'party', 'contacts']"
      v-model:filters="filters"
      filterDisplay="menu"
    >
      <template #header>
        <div class="table-header">
          <h3>Senators ({{ senators.length }})</h3>
          <IconField iconPosition="left">
            <InputIcon class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Search senators..." />
          </IconField>
        </div>
      </template>
      <Column field="name" header="Name" sortable :showFilterMatchModes="false">
        <template #body="{ data }">
          <span class="senator-name">{{ data.name }}</span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search by name" />
        </template>
      </Column>
      <Column field="party" header="Party" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search party" />
        </template>
      </Column>
      <Column field="contacts" header="Contacts" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search contacts" />
        </template>
      </Column>
      <Column header="Actions" :exportable="false">
        <template #body="{ data }">
          <div class="actions">
            <Button 
              icon="pi pi-pencil" 
              severity="info" 
              size="small"
              @click="openEditModal(data)" 
            />
            <Button 
              icon="pi pi-trash" 
              severity="danger" 
              size="small"
              @click="deleteSenator(data.id)" 
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog 
      v-model:visible="showModal" 
      :header="isEditing ? 'Edit Senator' : 'Add Senator'" 
      :modal="true" 
      :closable="false" 
      :dismissableMask="false"
      :style="{width: '500px'}"
    >
      <form @submit.prevent="saveSenator" class="modal-form">
        <div class="form-grid">
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="name"
                v-model="form.name" 
                :class="{ 'p-invalid': !form.name && formSubmitted }"
                required 
              />
              <label for="name">Name *</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="party"
                v-model="form.party" 
              />
              <label for="party">Party</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <Textarea 
                id="contacts"
                v-model="form.contacts" 
                rows="3"
              />
              <label for="contacts">Contacts</label>
            </IftaLabel>
          </div>
        </div>
        
        <div class="modal-actions">
          <Button 
            type="button" 
            label="Cancel" 
            severity="secondary" 
            icon="pi pi-times"
            @click="closeModal" 
          />
          <Button 
            type="submit" 
            :label="isEditing ? 'Update Senator' : 'Create Senator'" 
            icon="pi pi-check"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import IftaLabel from 'primevue/iftalabel'
import Swal from 'sweetalert2'
import api from '../axios.js'

// Reactive data
const selectedCongress = ref('')
const senators = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const loading = ref(false)
const formSubmitted = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  name: { operator: 'and', constraints: [{ value: null, matchMode: 'startsWith' }] },
  party: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  contacts: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const form = ref({
  id: null,
  name: '',
  party: '',
  contacts: ''
})

const congressOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' },
  { label: '18th Congress', value: '18' },
  { label: '17th Congress', value: '17' }
]

// Methods
const loadSenators = async () => {
  try {
    loading.value = true
    const response = await api.get(`/senators?congress_no=${selectedCongress.value}`)
    senators.value = response.data
  } catch (error) {
    console.error('Error loading senators:', error)
    senators.value = []
  } finally {
    loading.value = false
  }
}

const openAddModal = () => {
  isEditing.value = false
  form.value = { id: null, name: '', party: '', contacts: '' }
  showModal.value = true
}

const openEditModal = (senator) => {
  isEditing.value = true
  form.value = { ...senator }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formSubmitted.value = false
  saving.value = false
  form.value = { id: null, name: '', party: '', contacts: '' }
}

const saveSenator = async () => {
  formSubmitted.value = true
  
  if (!form.value.name) {
    return
  }
  
  const result = await Swal.fire({
    title: 'Confirm Action',
    text: 'Confirming',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Proceed',
    cancelButtonText: 'Cancel',
    target: document.body
  })
  
  if (!result.isConfirmed) {
    return
  }
  
  try {
    saving.value = true
    const data = {
      name: form.value.name,
      party: form.value.party,
      contacts: form.value.contacts,
      congress_no: selectedCongress.value
    }
    
    if (isEditing.value) {
      await api.put(`/senators/${form.value.id}`, data)
    } else {
      await api.post('/senators', data)
    }
    
    closeModal()
    loadSenators()
    
    Swal.fire({
      title: 'Success!',
      text: `Senator ${isEditing.value ? 'updated' : 'created'} successfully`,
      icon: 'success',
      confirmButtonText: 'OK',
      target: document.body
    })
  } catch (error) {
    console.error('Error saving senator:', error)
  } finally {
    saving.value = false
  }
}

const deleteSenator = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Senator',
    text: 'Are you sure you want to delete this senator?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
    target: document.body
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/senators/${id}`)
      loadSenators()
      
      Swal.fire({
        title: 'Deleted!',
        text: 'Senator has been deleted successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        target: document.body
      })
    } catch (error) {
      console.error('Error deleting senator:', error)
    }
  }
}

const loadCurrentCongress = async () => {
  try {
    const response = await api.get('/current-congress')
    selectedCongress.value = response.data.congress_no
  } catch (error) {
    selectedCongress.value = '20' // fallback
  }
}

// Lifecycle
onMounted(async () => {
  await loadCurrentCongress()
  loadSenators()
})
</script>

<style scoped>
.senators-page {
  padding: 2rem;
  /* max-width: 1200px; */
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.congress-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.congress-selector label {
  font-weight: 600;
  color: #1e3a8a;
}

.senator-name {
  font-weight: 600;
  color: #1e3a8a;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-dropdown,
.form-group .p-select {
  width: 100% !important;
  box-sizing: border-box;
}

.form-group .p-select .p-select-label {
  width: 100% !important;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .senators-page {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}
</style>